import { AlertTriangle, ArrowRight, KeyRound, ShieldCheck } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthStatusLayout from './components/auth/AuthStatusLayout';
import { logAuthEvent } from './lib/authLogger';
import { getSupabase, hasSupabaseConfig } from './lib/supabaseClient';

function parseHashParams(hash) {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  return new URLSearchParams(raw);
}

function getRecoveryType(searchParams, hashParams) {
  return (
    searchParams.get('type') ||
    hashParams.get('type') ||
    searchParams.get('mode') ||
    ''
  ).toLowerCase();
}

function getNextPath(searchParams, hashParams) {
  return searchParams.get('next') || hashParams.get('next') || '/';
}

function sanitizeNextPath(nextPath) {
  if (!nextPath || !nextPath.startsWith('/')) {
    return '/';
  }

  if (nextPath.startsWith('//')) {
    return '/';
  }

  return nextPath;
}

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('Processing your secure link...');
  const [errorMessage, setErrorMessage] = useState('');
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const hashParams = useMemo(() => parseHashParams(window.location.hash), []);

  useEffect(() => {
    let isMounted = true;

    async function completeAuth() {
      if (!hasSupabaseConfig()) {
        logAuthEvent('warn', '[auth.callback] Supabase config missing', {
          hasAuthCode: Boolean(params.get('code')),
          hasAccessToken: Boolean(hashParams.get('access_token')),
          hasRefreshToken: Boolean(hashParams.get('refresh_token')),
          flowType: getRecoveryType(params, hashParams) || 'unknown',
        });
        if (!isMounted) return;
        setErrorMessage(
          'Supabase is not configured for this build. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in your local .env or Cloudflare Pages build environment, then rebuild the site.'
        );
        setStatus('Authentication is unavailable.');
        return;
      }

      const authCode = params.get('code');
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      const flowType = getRecoveryType(params, hashParams);

      try {
        if (authCode) {
          const supabaseInstance = await getSupabase();
          const { error } = await supabaseInstance.auth.exchangeCodeForSession(authCode);
          if (error) throw error;
        } else if (accessToken && refreshToken) {
          const supabaseInstance = await getSupabase();
          const { error } = await supabaseInstance.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (error) throw error;
        }

        if (!isMounted) return;

        if (flowType === 'recovery') {
          setIsRecoveryMode(true);
          setStatus('Choose a new password to finish recovery.');
          window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
          return;
        }

        setStatus('Authentication complete. Redirecting...');
        const nextPath = sanitizeNextPath(getNextPath(params, hashParams));
        window.history.replaceState({}, document.title, nextPath);
        navigate(nextPath, { replace: true });
      } catch (error) {
        if (!isMounted) return;
        setErrorMessage(error?.message || 'Unable to complete authentication.');
        setStatus('Authentication failed.');
      }
    }

    completeAuth();

    return () => {
      isMounted = false;
    };
  }, [hashParams, navigate, params]);

  async function handlePasswordReset(event) {
    event.preventDefault();

    if (!(await hasSupabaseConfig())) {
      setErrorMessage('Supabase is not configured.');
      return;
    }

    if (password.length < 8) {
      setErrorMessage('Use at least 8 characters for the new password.');
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const supabaseInstance = await getSupabase();
      const { error } = await supabaseInstance.auth.updateUser({ password });
      if (error) throw error;

      setSuccessMessage('Password updated. Redirecting to home...');
      window.setTimeout(() => navigate('/', { replace: true }), 1200);
    } catch (error) {
      setErrorMessage(error?.message || 'Unable to update password.');
    } finally {
      setIsSubmitting(false);
    }
  }

  const title = successMessage
    ? 'Password updated'
    : isRecoveryMode
      ? 'Create a new password'
      : errorMessage
        ? 'We could not finish sign-in'
        : 'Finishing secure sign-in';

  const body = successMessage
    ? 'Your new password is saved. TECNIQ will take you back home in a moment.'
    : isRecoveryMode
      ? 'Set a new password to complete your recovery flow and return to the app with a clean handoff.'
      : errorMessage
        ? 'The secure handoff did not complete. You can safely head back to TECNIQ and request a fresh link if needed.'
        : 'TECNIQ is exchanging your secure link and preparing the next step.';

  const note = successMessage
    ? 'If the app is installed, you can continue there immediately after the redirect.'
    : isRecoveryMode
      ? 'Use at least 8 characters so the account is protected before we hand you back to the product.'
      : 'This branded route keeps recovery and magic-link flows off blank callback screens and inside the TECNIQ design system.';

  const statusLabel = successMessage
    ? 'Recovery complete'
    : isRecoveryMode
      ? 'Password reset'
      : errorMessage
        ? 'Authentication error'
        : 'Secure session handoff';

  const statusIcon = successMessage
    ? <ShieldCheck size={18} />
    : errorMessage
      ? <AlertTriangle size={18} />
      : isRecoveryMode
        ? <KeyRound size={18} />
        : <ShieldCheck size={18} />;

  return (
    <AuthStatusLayout
      tone={errorMessage ? 'error' : successMessage ? 'success' : 'neutral'}
      eyebrow="TECNIQ "
      title={title}
      body={body}
      note={note}
      statusLabel={statusLabel}
      icon={statusIcon}
      actions={[
        {
          label: 'Return to TECNIQ',
          href: '/',
          variant: 'secondary',
          icon: <ArrowRight size={18} />,
        },
      ]}
    >
      {isRecoveryMode ? (
        <form className="auth-status__form" onSubmit={handlePasswordReset}>
          <label className="auth-status__label" htmlFor="new-password">
            <span>New password</span>
            <input
              className="auth-status__input"
              id="new-password"
              type="password"
              autoComplete="new-password"
              placeholder="New password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>

          <label className="auth-status__label" htmlFor="confirm-password">
            <span>Confirm password</span>
            <input
              className="auth-status__input"
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
            />
          </label>

          <button className="tk-button tk-button--primary tk-button--full auth-status__submit" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Updating password…' : 'Update password'}
          </button>

          {errorMessage ? <p className="auth-status__form-error">{errorMessage}</p> : null}
          {successMessage ? <p className="auth-status__form-success">{successMessage}</p> : null}
        </form>
      ) : (
        <div className="auth-status__form">
          {errorMessage ? (
            <>
              <p className="auth-status__form-note">{status}</p>
              <p className="auth-status__form-error">{errorMessage}</p>
            </>
          ) : (
            <div className="auth-status__loading" aria-live="polite">
              <span className="auth-status__spinner" aria-hidden="true" />
              <span>{status}</span>
            </div>
          )}
        </div>
      )}
    </AuthStatusLayout>
  );
}
